import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { useWebSocket } from '../contexts/WebSocketContext';
import Navbar from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { Calendar, Users, Clock, MapPin, Search, ChevronLeft, Zap, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import DailyTournamentsClassic from './DailyTournamentsClassic';
import { TournamentPodium } from '../components/TournamentPodium';

const DailyTournamentsPage = () => {
  const [design, setDesign] = useState(localStorage.getItem('dailyPageDesign') || 'new');

  const toggleDesign = () => {
    const next = design === 'new' ? 'classic' : 'new';
    setDesign(next);
    localStorage.setItem('dailyPageDesign', next);
  };

  const ToggleBtn = () => (
    <button
      onClick={toggleDesign}
      data-testid="design-toggle-btn"
      className="fixed bottom-24 left-4 z-50 flex items-center gap-2 bg-white/90 dark:bg-slate-800/90 backdrop-blur border border-slate-200 dark:border-slate-700 shadow-lg rounded-full px-4 py-2.5 text-sm font-bold text-slate-700 dark:text-slate-200 hover:shadow-xl hover:scale-105 transition-all"
      title="החלף עיצוב עמוד"
    >
      <Palette className="w-4 h-4 text-[#1d6ff2]" />
      {design === 'new' ? 'לעיצוב הקלאסי' : 'לעיצוב החדש ✨'}
    </button>
  );

  if (design === 'classic') {
    return (
      <>
        <DailyTournamentsClassic />
        <ToggleBtn />
      </>
    );
  }
  return (
    <>
      <DailyTournamentsNew />
      <ToggleBtn />
    </>
  );
};

const DailyTournamentsNew = () => {
  const { API, user } = useContext(AuthContext);
  const { lastMessage } = useWebSocket();
  const [tournamentsByMonth, setTournamentsByMonth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('הכל');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 'הכל', label: 'הכל' },
    { id: 'דרג א', label: 'דרג א' },
    { id: 'דרג ב', label: 'דרג ב' },
    { id: 'דרג ג', label: 'דרג ג' },
    { id: 'יחידים open', label: 'יחידים Open' },
    { id: 'זוגות open', label: 'זוגות Open' },
    { id: 'נשים', label: 'נשים' },
  ];

  useEffect(() => {
    fetchTournaments();
    const interval = setInterval(fetchTournaments, 120000);
    return () => clearInterval(interval);
  }, [selectedCategory]);

  useEffect(() => {
    if (lastMessage && lastMessage.type === 'tournament_update') {
      fetchTournaments();
    }
  }, [lastMessage]);

  const fetchTournaments = async () => {
    try {
      const categoryParam = selectedCategory !== 'הכל' ? `?category=${encodeURIComponent(selectedCategory)}` : '';
      const response = await axios.get(`${API}/tournaments/daily/by-month${categoryParam}`);
      setTournamentsByMonth(response.data);
    } catch (error) {
      console.error('Failed to fetch tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (tournamentId) => {
    if (!user) {
      toast.error('עליך להיות מחובר כדי להירשם');
      return;
    }
    try {
      const response = await axios.post(`${API}/tournaments/register`, { tournament_id: tournamentId });
      toast.success(response.data.message);
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בהרשמה');
    }
  };

  const handleUnregister = async (tournamentId) => {
    try {
      const response = await axios.post(`${API}/tournaments/unregister`, { tournament_id: tournamentId });
      toast.success(response.data.message);
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בביטול');
    }
  };

  const isUserRegistered = (tournament) =>
    user && (tournament.registered_players?.includes(user.id) || tournament.waiting_list?.includes(user.id));

  const getStatusInfo = (t) => {
    if (t.status === 'upcoming') return { label: 'יפתח בקרוב', cls: 'bg-orange-500/10 text-orange-600 border border-orange-500/25', dot: 'bg-orange-500' };
    if (t.status === 'in_progress') return { label: 'LIVE עכשיו', cls: 'bg-red-500/10 text-red-600 border border-red-500/25 animate-pulse', dot: 'bg-red-500' };
    if (t.status === 'completed') return { label: 'הסתיים', cls: 'bg-slate-100 text-slate-500 border border-slate-200', dot: 'bg-slate-400' };
    if (t.status === 'full') return { label: 'מלא', cls: 'bg-red-500/10 text-red-600 border border-red-500/25', dot: 'bg-red-500' };
    return { label: 'פתוח להרשמה', cls: 'bg-[#10b47e]/10 text-[#0da06f] border border-[#10b47e]/25', dot: 'bg-[#10b47e]' };
  };

  const categoryAccent = (cat = '') => {
    if (cat.includes('דרג א')) return 'from-amber-400 to-orange-500';
    if (cat.includes('דרג ב')) return 'from-[#1d6ff2] to-cyan-400';
    if (cat.includes('דרג ג')) return 'from-violet-500 to-purple-400';
    if (cat.includes('זוגות')) return 'from-indigo-500 to-[#1d6ff2]';
    if (cat.includes('נשים')) return 'from-pink-500 to-rose-400';
    return 'from-[#10b47e] to-emerald-400';
  };

  const filterBySearch = (list = []) =>
    searchTerm.trim()
      ? list.filter((t) => (t.name || '').toLowerCase().includes(searchTerm.trim().toLowerCase()))
      : list;

  const visibleMonths = tournamentsByMonth
    .map((m) => ({ ...m, tournaments: filterBySearch(m.tournaments) }))
    .filter((m) => m.tournaments && m.tournaments.length > 0);

  return (
    <div className="min-h-screen bg-[#f8fafc] dark:bg-slate-950">
      <Navbar />

      {/* Header with stadium glow */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 dark:opacity-10"
          style={{ backgroundImage: "url('/hero-court-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(6px)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/80 to-[#f8fafc] dark:from-slate-950/70 dark:via-slate-950/85 dark:to-slate-950" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-10 text-center">
          <div className="inline-flex items-center gap-2 bg-[#1d6ff2]/10 text-[#1d6ff2] border border-[#1d6ff2]/20 rounded-full px-4 py-1.5 text-sm font-bold mb-5">
            <Zap className="w-4 h-4" />
            יום אחד · עד 16 שחקנים · אקשן מלא
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1e2b5e] dark:text-white leading-tight mb-3"
            data-testid="daily-tournaments-title"
          >
            תחרויות יומיות
          </h1>
          <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 max-w-xl mx-auto">
            🎾 נכנסים בבוקר, יוצאים עם גביע — תחרויות שמתחילות ומסתיימות באותו היום
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        {/* Sticky glass filter bar */}
        <div className="sticky top-4 z-40 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl border border-slate-200 dark:border-slate-800 rounded-2xl p-3 sm:p-4 shadow-[0_8px_30px_rgb(0,0,0,0.06)] mb-12">
          <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
            <div className="relative w-full lg:max-w-xs shrink-0">
              <Search className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
              <input
                type="text"
                placeholder="חפש תחרות..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2.5 rounded-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1d6ff2] focus:border-transparent transition-shadow text-sm"
                data-testid="search-input"
              />
            </div>
            <div className="flex overflow-x-auto gap-2 pb-1 lg:pb-0" style={{ scrollbarWidth: 'none' }} data-testid="category-tabs">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  data-testid={`category-tab-${category.id}`}
                  className={`rounded-full px-5 py-2 whitespace-nowrap text-sm font-bold transition-all duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-[#1e2b5e] text-white shadow-md dark:bg-[#1d6ff2] scale-[1.03]'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8 mb-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white dark:bg-slate-900 rounded-3xl p-6 animate-pulse border border-slate-100 dark:border-slate-800">
                  <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded mb-5"></div>
                  <div className="h-6 bg-slate-200 dark:bg-slate-800 rounded-full w-3/4 mb-4"></div>
                  <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded-full w-1/2 mb-3"></div>
                  <div className="space-y-2">
                    <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded-full w-full"></div>
                    <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded-full w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center">
              <LoadingSpinner size="lg" text="טוען תחרויות יומיות..." />
            </div>
          </div>
        ) : visibleMonths.length > 0 ? (
          <div className="space-y-16">
            {visibleMonths.map((monthData, monthIdx) => (
              <div key={monthIdx} className="animate-page-in" style={{ animationDelay: `${monthIdx * 0.1}s` }}>
                {/* Month header */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1d6ff2] to-[#10b47e] flex items-center justify-center shadow-lg shadow-[#1d6ff2]/20 shrink-0">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100 whitespace-nowrap">{monthData.month_display}</h2>
                  <div className="h-px flex-1 bg-gradient-to-l from-slate-200 to-transparent dark:from-slate-800" />
                  <span className="text-sm text-slate-400 font-medium shrink-0">{monthData.tournaments.length} תחרויות</span>
                </div>

                {/* Cards grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
                  {monthData.tournaments.map((tournament, tIdx) => {
                    const statusInfo = getStatusInfo(tournament);
                    const isDoubles = tournament.category?.includes('זוגות');
                    const regCount = isDoubles
                      ? (tournament.registered_players_names?.length || Math.ceil((tournament.registered_players?.length || 0) / 2))
                      : (tournament.registered_players?.length || 0);
                    const maxCount = tournament.max_participants || tournament.max_players || 16;
                    const fillPct = Math.min(100, Math.round((regCount / maxCount) * 100));
                    const dateObj = new Date(tournament.date);

                    return (
                      <div key={tournament.id} className="list-item-animate" style={{ animationDelay: `${tIdx * 0.06}s` }}>
                        <div
                          className="h-full bg-white dark:bg-slate-900 rounded-3xl overflow-hidden border border-slate-100 dark:border-slate-800 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-xl hover:-translate-y-1 hover:border-[#1d6ff2]/30 transition-all duration-300 group flex flex-col relative"
                          data-testid={`tournament-card-${tournament.id}`}
                        >
                          {/* Category accent strip */}
                          <div className={`h-1.5 w-full bg-gradient-to-l ${categoryAccent(tournament.category)}`} />

                          {/* Card header */}
                          <Link to={`/tournament/${tournament.id}`} className="block px-5 pt-5">
                            <div className="flex items-start justify-between gap-3">
                              {/* Date block */}
                              <div className="shrink-0 w-16 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 text-center py-2">
                                <div className="text-2xl font-black text-[#1e2b5e] dark:text-white leading-none">{dateObj.getDate()}</div>
                                <div className="text-[11px] font-bold text-[#1d6ff2] mt-1">{dateObj.toLocaleDateString('he-IL', { month: 'short' })}</div>
                                <div className="text-[10px] text-slate-400">{dateObj.toLocaleDateString('he-IL', { weekday: 'short' })}</div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${statusInfo.cls}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${statusInfo.dot}`} />
                                  {statusInfo.label}
                                </span>
                                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 leading-snug mt-1.5 group-hover:text-[#1d6ff2] transition-colors line-clamp-2">
                                  {tournament.name}
                                </h3>
                                <p className="text-sm text-slate-400 font-medium">{tournament.category}</p>
                              </div>
                            </div>
                          </Link>

                          <div className="px-5 pb-5 pt-4 flex flex-col gap-3 flex-1">
                            {/* Time + location */}
                            <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400 flex-wrap">
                              {tournament.start_time && (
                                <span className="flex items-center gap-1.5">
                                  <Clock className="w-4 h-4 text-[#1d6ff2]" />
                                  <span className="font-bold">{tournament.start_time}</span>
                                </span>
                              )}
                              {tournament.location && (
                                <span className="flex items-center gap-1.5 min-w-0">
                                  <MapPin className="w-4 h-4 text-[#10b47e] shrink-0" />
                                  <span className="truncate">{tournament.location}</span>
                                </span>
                              )}
                            </div>

                            {/* Capacity progress */}
                            <div>
                              <div className="flex items-center justify-between text-sm mb-1.5">
                                <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                                  <Users className="w-4 h-4 text-[#1e2b5e] dark:text-slate-300" />
                                  {isDoubles ? 'זוגות' : 'משתתפים'}
                                </span>
                                <span className="font-bold text-slate-700 dark:text-slate-200">
                                  {regCount}<span className="text-slate-400 font-medium">/{maxCount}</span>
                                </span>
                              </div>
                              <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-500 ${fillPct >= 100 ? 'bg-red-500' : fillPct >= 75 ? 'bg-gradient-to-l from-orange-400 to-amber-400' : 'bg-gradient-to-l from-[#10b47e] to-emerald-400'}`}
                                  style={{ width: `${fillPct}%` }}
                                />
                              </div>
                            </div>

                            {/* Player chips */}
                            {tournament.registered_players_names && tournament.registered_players_names.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {tournament.registered_players_names.map((name, idx) => (
                                  <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                                    {name}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Waiting list */}
                            {tournament.waiting_list && tournament.waiting_list.length > 0 && (
                              <div data-testid={`waiting-list-names-${tournament.id}`} className="bg-orange-500/5 border border-orange-500/20 p-2.5 rounded-xl">
                                <div className="flex items-center justify-between text-orange-600 mb-1">
                                  <span className="text-xs font-bold">⏳ רשימת המתנה</span>
                                  <span className="font-black text-xs">{tournament.waiting_list.length}</span>
                                </div>
                                {tournament.waiting_list_names && tournament.waiting_list_names.length > 0 && (
                                  <p className="text-xs text-orange-500/90 leading-relaxed">{tournament.waiting_list_names.join(', ')}</p>
                                )}
                              </div>
                            )}

                            {tournament.status === 'full' && (
                              <div data-testid={`waiting-list-msg-${tournament.id}`} className="text-center py-2 px-3 bg-amber-500/5 border border-amber-500/25 rounded-xl">
                                <p className="text-amber-700 dark:text-amber-400 text-xs font-bold" dir="rtl">📋 ניתן עדיין להירשם לרשימת ההמתנה!</p>
                              </div>
                            )}

                            {tournament.status === 'in_progress' && !isUserRegistered(tournament) && (
                              <div data-testid={`live-closed-${tournament.id}`} className="px-3 py-2 bg-red-500/5 border border-red-500/25 rounded-xl text-center">
                                <p className="text-red-600 font-bold text-xs">התחרות החלה — ההרשמה סגורה</p>
                              </div>
                            )}

                            {/* פודיום זוכים */}
                            <TournamentPodium tournament={tournament} />

                            {/* Actions - pinned to bottom */}
                            <div className="mt-auto pt-1 space-y-2">
                              <div className="flex gap-2">
                                {isDoubles ? (
                                  <>
                                    {user && tournament.status !== 'completed' && isUserRegistered(tournament) && (
                                      <Button
                                        variant="destructive"
                                        className="flex-1 rounded-xl font-semibold"
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleUnregister(tournament.id); }}
                                        data-testid={`unregister-btn-${tournament.id}`}
                                      >
                                        הסר אותי
                                      </Button>
                                    )}
                                    <Link to={`/tournament/${tournament.id}`} className="flex-1">
                                      <Button data-testid={`doubles-details-btn-${tournament.id}`} className="w-full bg-[#1e2b5e] hover:bg-[#16204a] dark:bg-[#1d6ff2] dark:hover:bg-[#1660d8] text-white rounded-xl py-3 font-semibold transition-transform active:scale-[0.98]">
                                        {tournament.status === 'in_progress' ? 'צפה בפרטים' : 'פרטים והרשמה'}
                                        <ChevronLeft className="w-4 h-4 mr-1" />
                                      </Button>
                                    </Link>
                                  </>
                                ) : (
                                  <>
                                    {user && tournament.status !== 'completed' && tournament.status !== 'in_progress' && (
                                      isUserRegistered(tournament) ? (
                                        <Button
                                          variant="destructive"
                                          className="flex-1 rounded-xl font-semibold"
                                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleUnregister(tournament.id); }}
                                          data-testid={`unregister-btn-${tournament.id}`}
                                        >
                                          הסר אותי
                                        </Button>
                                      ) : (
                                        <Button
                                          className="flex-1 bg-[#10b47e] hover:bg-[#0da06f] text-white rounded-xl py-3 font-semibold shadow-sm shadow-[#10b47e]/25 transition-transform active:scale-[0.98]"
                                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRegister(tournament.id); }}
                                          data-testid={`register-btn-${tournament.id}`}
                                        >
                                          הירשם עכשיו 🎾
                                        </Button>
                                      )
                                    )}
                                    <Link to={`/tournament/${tournament.id}`} className="flex-1">
                                      <Button variant="ghost" className="w-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl py-3 font-medium">
                                        צפה בפרטים
                                        <ChevronLeft className="w-4 h-4 mr-1" />
                                      </Button>
                                    </Link>
                                  </>
                                )}
                              </div>

                              {tournament.status !== 'completed' && (
                                <a
                                  href="https://links.payboxapp.com/5WsY9sjQLUb"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Button className="w-full bg-[#00c2e6] hover:bg-[#00aed0] text-white rounded-xl py-3 font-semibold transition-transform active:scale-[0.98]">
                                    💳 לתשלום דרך Paybox
                                  </Button>
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-24">
            <span className="text-6xl block mb-4">🎾</span>
            <p className="text-xl font-bold text-slate-600 dark:text-slate-300 mb-1">
              {searchTerm ? 'לא נמצאו תחרויות מתאימות לחיפוש' : 'אין תחרויות בקטגוריה זו'}
            </p>
            <p className="text-sm text-slate-400">נסה קטגוריה אחרת או חזור לבדוק בקרוב</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyTournamentsPage;
